import { useState, useRef, useEffect } from 'react';
import {
  Stack,
  Text,
  Card,
  TextInput,
  Button,
  Group,
  Paper,
  ScrollArea,
  ThemeIcon,
  Loader,
  ActionIcon,
} from '@mantine/core';
import {
  IconSend,
  IconRobot,
  IconUser,
  IconShoppingCart,
  IconSparkles,
  IconHeadset,
  IconPackage,
  IconFileInvoice,
  IconTruck,
} from '@tabler/icons-react';
import { useAIChatSend } from '@/hooks/usePortalApi';
import { useAuthStore } from '@/store/auth';

interface ChatMessage {
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
  actions?: { tool: string; result?: Record<string, unknown> }[];
}

const ACTION_ICONS: Record<string, typeof IconPackage> = {
  check_order_status: IconPackage,
  track_shipment: IconTruck,
  get_invoice_balance: IconFileInvoice,
  submit_reorder: IconPackage,
  search_products: IconPackage,
};

function ActionResultCard({ action }: { action: { tool: string; result?: Record<string, unknown> } }) {
  const Icon = ACTION_ICONS[action.tool] || IconPackage;
  const result = action.result || {};

  return (
    <Card padding="xs" radius="sm" withBorder mt={4}>
      <Group gap="xs" mb={4}>
        <ThemeIcon size="xs" variant="light" color="brand">
          <Icon size={10} />
        </ThemeIcon>
        <Text size="xs" fw={600} tt="capitalize">
          {action.tool.replace(/_/g, ' ')}
        </Text>
      </Group>
      {Object.entries(result).map(([key, value]) => (
        <Group key={key} gap={4}>
          <Text size="xs" c="dimmed" tt="capitalize">
            {key.replace(/_/g, ' ')}:
          </Text>
          <Text size="xs" fw={500}>
            {String(value)}
          </Text>
        </Group>
      ))}
    </Card>
  );
}

export default function AIAssistant() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('ai-assistant-session-id');
    } catch {
      return null;
    }
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  const chatMutation = useAIChatSend();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;

    const userMsg: ChatMessage = { role: 'user', content: input.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    const message = input.trim();
    setInput('');

    chatMutation.mutate(
      {
        message,
        session_id: sessionId || undefined,
        customer_id: (user as any)?.customer?.id || 0,
      },
      {
        onSuccess: (resp: any) => {
          const result = resp?.data || resp;
          if (result.session_id) {
            setSessionId(result.session_id);
            try {
              sessionStorage.setItem('ai-assistant-session-id', result.session_id);
            } catch { /* noop */ }
          }
          const botMsg: ChatMessage = {
            role: 'bot',
            content: result.response || 'Sorry, I could not process your request.',
            timestamp: new Date(),
            actions: result.actions_taken,
          };
          setMessages(prev => [...prev, botMsg]);
        },
        onError: () => {
          setMessages(prev => [
            ...prev,
            { role: 'bot', content: 'Sorry, something went wrong. Please try again.', timestamp: new Date() },
          ]);
        },
      }
    );
  };

  const handleQuickAction = (text: string) => {
    setInput(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Stack gap="md" style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Group>
        <ThemeIcon size={44} radius="xl" variant="light" color="brand">
          <IconRobot size={24} />
        </ThemeIcon>
        <div>
          <Text size="xl" fw={700}>AI Assistant</Text>
          <Text size="sm" c="dimmed">Your intelligent assistant for orders, products, and account help</Text>
        </div>
      </Group>

      {/* Chat Area */}
      <Paper shadow="sm" radius="md" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ScrollArea style={{ flex: 1 }} p="md" viewportRef={scrollRef}>
          <Stack gap="sm">
            {/* Empty state */}
            {messages.length === 0 && (
              <Card padding="xl" radius="md" bg="gray.0" mt="xl">
                <Stack align="center" gap="sm">
                  <ThemeIcon size={56} variant="light" color="brand" radius="xl">
                    <IconRobot size={30} />
                  </ThemeIcon>
                  <Text size="lg" fw={600} ta="center">
                    Welcome! How can I help you today?
                  </Text>
                  <Text size="sm" c="dimmed" ta="center" maw={400}>
                    I can help with smart reorders based on your purchase history, personalized product
                    recommendations, order tracking, and account questions.
                  </Text>
                  <Group gap="xs" mt="sm">
                    {['Check my orders', 'Track a shipment', 'Invoice balance'].map((q) => (
                      <Button
                        key={q}
                        size="xs"
                        variant="light"
                        radius="xl"
                        onClick={() => handleQuickAction(q)}
                      >
                        {q}
                      </Button>
                    ))}
                  </Group>
                </Stack>
              </Card>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <Group
                key={i}
                align="flex-start"
                justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                gap="xs"
              >
                {msg.role === 'bot' && (
                  <ThemeIcon size="sm" variant="light" color="brand" radius="xl" mt={2}>
                    <IconRobot size={12} />
                  </ThemeIcon>
                )}
                <Paper
                  p="sm"
                  radius="md"
                  style={{
                    maxWidth: '75%',
                    background:
                      msg.role === 'user'
                        ? 'var(--mantine-color-brand-6)'
                        : 'var(--mantine-color-gray-1)',
                  }}
                >
                  <Text
                    size="sm"
                    c={msg.role === 'user' ? 'white' : undefined}
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {msg.content}
                  </Text>

                  {msg.actions && msg.actions.length > 0 && (
                    <Stack gap={4} mt="xs">
                      {msg.actions.map((action, ai) => (
                        <ActionResultCard key={ai} action={action} />
                      ))}
                    </Stack>
                  )}

                  <Text
                    size="xs"
                    c={msg.role === 'user' ? 'rgba(255,255,255,0.7)' : 'dimmed'}
                    ta="right"
                    mt={4}
                  >
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                </Paper>
                {msg.role === 'user' && (
                  <ThemeIcon size="sm" variant="light" color="gray" radius="xl" mt={2}>
                    <IconUser size={12} />
                  </ThemeIcon>
                )}
              </Group>
            ))}

            {/* Typing indicator */}
            {chatMutation.isPending && (
              <Group gap="xs">
                <ThemeIcon size="sm" variant="light" color="brand" radius="xl">
                  <IconRobot size={12} />
                </ThemeIcon>
                <Paper p="xs" radius="md" bg="gray.1">
                  <Group gap={4}>
                    <Loader size="xs" type="dots" />
                    <Text size="xs" c="dimmed">Typing...</Text>
                  </Group>
                </Paper>
              </Group>
            )}
          </Stack>
        </ScrollArea>

        {/* Input area */}
        <Group p="md" gap="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <TextInput
            placeholder="Type your question..."
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
            size="md"
            disabled={chatMutation.isPending}
          />
          <ActionIcon
            size="lg"
            variant="filled"
            color="brand"
            onClick={handleSend}
            disabled={!input.trim() || chatMutation.isPending}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Group>
      </Paper>

      {/* Quick Action Cards - shown below chat */}
      {messages.length === 0 && (
        <Group grow>
          <Card shadow="xs" radius="md" withBorder p="md" style={{ cursor: 'pointer' }} onClick={() => handleQuickAction('Show me smart reorder suggestions based on my purchase history')}>
            <Group gap="sm">
              <ThemeIcon size={36} variant="light" color="green" radius="md">
                <IconShoppingCart size={20} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>Smart Reorder</Text>
                <Text size="xs" c="dimmed">AI-suggested items based on your purchase history</Text>
              </div>
            </Group>
          </Card>
          <Card shadow="xs" radius="md" withBorder p="md" style={{ cursor: 'pointer' }} onClick={() => handleQuickAction('Show me personalized product recommendations')}>
            <Group gap="sm">
              <ThemeIcon size={36} variant="light" color="violet" radius="md">
                <IconSparkles size={20} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>Recommendations</Text>
                <Text size="xs" c="dimmed">Personalized product recommendations</Text>
              </div>
            </Group>
          </Card>
          <Card shadow="xs" radius="md" withBorder p="md" style={{ cursor: 'pointer' }} onClick={() => handleQuickAction('I need help with my account')}>
            <Group gap="sm">
              <ThemeIcon size={36} variant="light" color="orange" radius="md">
                <IconHeadset size={20} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>Account Help</Text>
                <Text size="xs" c="dimmed">Get help with billing, deliveries, and more</Text>
              </div>
            </Group>
          </Card>
        </Group>
      )}
    </Stack>
  );
}
